import { CABLE_TYPE_LABELS, type CableType } from './cable.ts';
import {
  PORT_STATE_HEX as DEVICE_STATE_HEX,
  PORT_STATE_HEX_DARK as DEVICE_STATE_HEX_DARK,
} from './port-state.ts';

// The one colour key the cabling drawings share. Two things live here so the
// port faceplate, the wiring diagram, the panel schedule, and the report all
// speak the same colours instead of each hard-coding its own:
//
//   1. The termination state of a panel position (free, linked, landed, half —
//      "half" is one side of a panel patched, which a plain device port cannot
//      be, so the panel schedule keeps its own four names). Each paints in the
//      shared semantic colour of the device-port state it corresponds to, taken
//      from shared/port-state.ts (which mirrors the --port-* tokens in
//      tokens.css): a run patched all the way through is "complete" green, one
//      side only is "incomplete" amber, a landed run keeps its blue, and a free
//      position the grey. A server-side SVG (the report, which has no stylesheet
//      and no CSS variables) reads these resolved hexes, so print and screen
//      never disagree.
//
//   2. The colour a run is drawn in on the wiring map when it is keyed by
//      cable type (Cat6 one colour, OM4 another), with a stable order for the
//      legend. A run may still carry its own chosen colour (DROP_COLOR_HEX);
//      cable-type colouring is the alternative the wiring diagram offers.

/** The state of a panel position, or of one end of a run, as the schedule names them. */
export const PORT_STATES = ['free', 'linked', 'landed', 'half'] as const;
export type PortStateName = (typeof PORT_STATES)[number];

export const PORT_STATE_LABELS: Readonly<Record<PortStateName, string>> = {
  free: 'Free',
  linked: 'Patched through',
  landed: 'Landed, not patched',
  half: 'Patched one side',
};

/**
 * Light-theme hex of each panel state, mapped to the shared device-port
 * colours (port-state.ts / tokens.css) so the schedule and report match the
 * live faceplate: linked→complete, half→incomplete, free→none.
 */
export const PORT_STATE_HEX: Readonly<Record<PortStateName, string>> = {
  free: DEVICE_STATE_HEX.none,
  linked: DEVICE_STATE_HEX.complete,
  landed: DEVICE_STATE_HEX.landed,
  half: DEVICE_STATE_HEX.incomplete,
};

/** Dark-theme hex of each panel state, from the dark half of the same colours. */
export const PORT_STATE_HEX_DARK: Readonly<Record<PortStateName, string>> = {
  free: DEVICE_STATE_HEX_DARK.none,
  linked: DEVICE_STATE_HEX_DARK.complete,
  landed: DEVICE_STATE_HEX_DARK.landed,
  half: DEVICE_STATE_HEX_DARK.incomplete,
};

/** Cable types in the order the wiring legend lists them. */
export const CABLE_TYPE_ORDER: readonly CableType[] = [
  'cat5e',
  'cat6',
  'cat6a',
  'cat7',
  'fiber_om3',
  'fiber_om4',
  'fiber_os2',
  'coax_rg6',
  'speaker_16_2',
  'speaker_14_2',
  'hdmi',
  'other',
];

/**
 * The stroke a run is drawn in when the wiring map is keyed by cable type.
 * Copper Ethernet keeps a family of blues, fiber the ambers and a violet,
 * and the rest their own hue; every value is a mid-tone that reads on both
 * the light and the dark ground the diagram is shown against.
 */
export const CABLE_TYPE_HEX: Readonly<Record<CableType, string>> = {
  cat5e: '#3b82f6',
  cat6: '#2457d6',
  cat6a: '#1e3a8a',
  cat7: '#0ea5e9',
  fiber_om3: '#f59e0b',
  fiber_om4: '#ea580c',
  fiber_os2: '#7c3aed',
  coax_rg6: '#78716c',
  speaker_16_2: '#16a34a',
  speaker_14_2: '#15803d',
  hdmi: '#db2777',
  other: '#6b7280',
};

/** The legend rows for a set of cable types, in the fixed order, labels and all. */
export function cableTypeLegend(
  types: Iterable<CableType>,
): { readonly type: CableType; readonly label: string; readonly color: string }[] {
  const present = new Set(types);
  return CABLE_TYPE_ORDER.filter((type) => present.has(type)).map((type) => ({
    type,
    label: CABLE_TYPE_LABELS[type],
    color: CABLE_TYPE_HEX[type],
  }));
}
